import {Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, Renderer2, ViewChild} from '@angular/core';
import {HorizontalPositionOfColorCreelPosition} from '@domain/textile-data/color-set/horizontal-position-of-color-creel-position';
import {HorizontalPositionOfGroupCreelPosition} from '@domain/textile-data/colored-yarn-set/horizontal-position-of-group-creel-position';
import {CreelPosition} from '@domain/textile-data/creel/creel-position';
import {HorizontalPositionOfCreelPosition} from '@domain/textile-data/creel/horizontal-position-of-creel-position';
import {MaterialSet} from '@domain/textile-data/textile-data';
import {HorizontalPositionOfYarnTypeCreelPosition} from '@domain/textile-data/yarn-set/horizontal-position-of-yarn-type-creel-position';
import {OverviewListYarnType} from '@presentation/pages/textile-data/yarn-type/overview/overview-list-yarn-type';
import {Color} from '@vdw/angular-component-library';
import {each, filter, indexOf, isEmpty, isEqual, range, size} from 'lodash-es';

@Component({
  selector: 'app-creel-position',
  templateUrl: './creel-position.component.html',
  styleUrls: ['./creel-position.component.scss']
})
export class CreelPositionComponent implements OnInit, OnDestroy {
  @Input() public creelPosition: CreelPosition;
  @Input() public creelPositionIndex: number;
  @Input() public materialSet: MaterialSet;
  @Input() public canShowError = false;
  @Input() public canEditMaterialSet: boolean;
  @Output() public selectedCreelPositionChanged: EventEmitter<void> = new EventEmitter<void>();
  @Output() public deselectAllSelectedCreelPositions: EventEmitter<void> = new EventEmitter<void>();
  @Output() public creelPositionHasBeenChanged: EventEmitter<void> = new EventEmitter<void>();
  @ViewChild('creelPositionElement', {static: true}) public creelPositionElement: ElementRef;

  private selectedCreelPosition: CreelPosition;
  private onDropListener: () => void;
  private onDragEnterListener: () => void;
  private onDragLeaveListener: () => void;
  private onDragOverListener: () => void;

  private readonly renderer: Renderer2;
  private readonly dropIndicatorClass = 'drop-indicator';

  public constructor(renderer: Renderer2) {
    this.renderer = renderer;
  }

  public ngOnInit(): void {
    this.attachListeners();
  }

  public ngOnDestroy(): void {
    this.detachListeners();
  }

  public onCreelPositionClick(selectedCreelPosition: CreelPosition, isSelected: boolean, event: MouseEvent): void {
    const {metaKey, altKey, shiftKey} = event;

    if (metaKey || altKey) {
      this.onMetaOrAltKeyDown(selectedCreelPosition, isSelected);
    } else if (shiftKey) {
      this.onShiftKeyDown(selectedCreelPosition);
    } else {
      this.onNoKeyDown(selectedCreelPosition, isSelected);
    }

    this.selectedCreelPositionChanged.emit();
  }

  public canShowWarning(creelPosition: CreelPosition): boolean {
    return this.canShowError && (isEmpty(creelPosition.horizontalPositionsOfCreelPosition) || this.containsGroupWithNoOrOneMaterial(creelPosition.horizontalPositionsOfCreelPosition));
  }

  private containsGroupWithNoOrOneMaterial(horizontalPositionsOfCreelPosition: HorizontalPositionOfCreelPosition[]): boolean {
    let result = false;

    for (const horizontalPositionOfCreelPosition of horizontalPositionsOfCreelPosition) {
      if (horizontalPositionOfCreelPosition.isGroup()) {
        const horizontalPositionsOfGroupCreelPosition: HorizontalPositionOfCreelPosition[] = (horizontalPositionOfCreelPosition as HorizontalPositionOfGroupCreelPosition).group;

        if (size(horizontalPositionsOfGroupCreelPosition) <= 1 || this.containsGroupWithNoOrOneMaterial(horizontalPositionsOfGroupCreelPosition)) {
          result = true;
          break;
        }
      }
    }

    return result;
  }

  private onDrop(event: any): void {
    const materialType = JSON.parse(event.dataTransfer.getData('materialType'));
    let horizontalPositionOfMaterialSetPosition: HorizontalPositionOfColorCreelPosition | HorizontalPositionOfYarnTypeCreelPosition;

    if (isEqual(materialType.type, 'color')) {
      const color: Color = materialType.data as Color;

      horizontalPositionOfMaterialSetPosition = new HorizontalPositionOfColorCreelPosition(1, new Color(color.id, color.name, color.rgb, color.used));
    } else if (isEqual(materialType.type, 'yarnType')) {
      const yarnType: OverviewListYarnType = materialType.data as OverviewListYarnType;

      horizontalPositionOfMaterialSetPosition = new HorizontalPositionOfYarnTypeCreelPosition(
        1,
        new OverviewListYarnType(yarnType.id, yarnType.name, yarnType.yarnCount, yarnType.yarnCountUnit, yarnType.threads)
      );
    }

    const repeatedHorizontalCreelPosition = filter(this.creelPosition.horizontalPositionsOfCreelPosition, (horizontalPosition: HorizontalPositionOfCreelPosition) =>
      isEqual(horizontalPosition, horizontalPositionOfMaterialSetPosition)
    );
    if (isEmpty(repeatedHorizontalCreelPosition)) {
      this.creelPosition.horizontalPositionsOfCreelPosition.unshift(horizontalPositionOfMaterialSetPosition);
      this.creelPositionHasBeenChanged.emit();
    }

    this.renderer.removeClass(this.creelPositionElement.nativeElement, this.dropIndicatorClass);
  }

  private onDragEnter(event: any): void {
    event.stopPropagation();
    this.renderer.addClass(this.creelPositionElement.nativeElement, this.dropIndicatorClass);
  }

  private onDragLeave(event: any): void {
    event.stopPropagation();
    this.renderer.removeClass(this.creelPositionElement.nativeElement, this.dropIndicatorClass);
  }

  private onDragOver(event: any): void {
    event.preventDefault();
  }

  private attachListeners(): void {
    this.onDropListener = this.renderer.listen(this.creelPositionElement.nativeElement, 'drop', (event: any) => this.onDrop(event));
    this.onDragEnterListener = this.renderer.listen(this.creelPositionElement.nativeElement, 'dragenter', (event: any) => this.onDragEnter(event));
    this.onDragLeaveListener = this.renderer.listen(this.creelPositionElement.nativeElement, 'dragleave', (event: any) => this.onDragLeave(event));
    this.onDragOverListener = this.renderer.listen(this.creelPositionElement.nativeElement, 'dragover', (event: any) => this.onDragOver(event));
  }

  private detachListeners(): void {
    this.onDropListener();
    this.onDragEnterListener();
    this.onDragLeaveListener();
    this.onDragOverListener();
  }

  private onMetaOrAltKeyDown(selectedCreelPosition: CreelPosition, isSelected: boolean): void {
    this.selectedCreelPosition = selectedCreelPosition;
    selectedCreelPosition.isSelected = !isSelected;
  }

  private onShiftKeyDown(selectedCreelPosition: CreelPosition): void {
    const indexOfFirstSelectedCreelPosition = indexOf(this.materialSet.creelPositions, this.selectedCreelPosition);
    let indexOfCurrentlySelectedCreelPosition = indexOf(this.materialSet.creelPositions, selectedCreelPosition);

    if (!isEqual(indexOfFirstSelectedCreelPosition, -1)) {
      this.deselectAllSelectedCreelPositions.emit();

      if (indexOfFirstSelectedCreelPosition < indexOfCurrentlySelectedCreelPosition) {
        indexOfCurrentlySelectedCreelPosition++;
      } else {
        indexOfCurrentlySelectedCreelPosition--;
      }

      each(range(indexOfFirstSelectedCreelPosition, indexOfCurrentlySelectedCreelPosition), (index: number) => {
        this.materialSet.creelPositions[index].isSelected = true;
      });
    }
  }

  private onNoKeyDown(selectedCreelPosition: CreelPosition, isSelected: boolean): void {
    const hasMoreThanOneCreelPositionSelected = filter(this.materialSet.creelPositions, ['isSelected', true]);
    this.deselectAllSelectedCreelPositions.emit();

    selectedCreelPosition.isSelected = isEmpty(hasMoreThanOneCreelPositionSelected) || isEqual(size(hasMoreThanOneCreelPositionSelected), 1) ? !isSelected : true;

    this.selectedCreelPosition = selectedCreelPosition;
  }
}
